import GenericFollowupFormComponent from 'src/components/followup/generic-followup-form.component'
import { CurecallModalContainer, CurecallConfirmationModal } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ErrorQueryHandler } from '../error/error-query-handler.component'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { PatientService } from 'src/services/patient.service'
import { createContext, useContext, useRef, useCallback, useState, useEffect } from 'react'

type TFollowupModalContext = {
  setHasFormBeenModified: (value: boolean) => void
  handleOnToggle: (event?: React.MouseEvent<HTMLElement>, isSubmittingForm?: boolean) => void
}

export const FollowupModalContext = createContext<TFollowupModalContext>({
  setHasFormBeenModified: () => {},
  handleOnToggle: () => {},
})
export const useFollowupModalContext = () => useContext(FollowupModalContext)

type TFollowupModalProps = {
  patientId: number
  patientFollowupIdToEdit: number
  onToggle: (event?: React.MouseEvent<HTMLElement>) => void
}
const GenericFollowupModal = ({ patientId, patientFollowupIdToEdit, onToggle }: TFollowupModalProps) => {
  const { t } = useTranslation()
  const { project, getFollowupById } = useCurrentProject()
  const {
    isLoading: isLoadingPatient,
    error: errorPatient,
    data: patient,
  } = useQuery({
    queryKey: ['patients', project.id, patientId],
    queryFn: () => PatientService.getById(project.id, patientId),
    staleTime: 60 * 2000, // won't fetch patient again before 2 minutes
  })

  // Derive followup data here (not in hooks) so it's available for useEffect below.
  const currentFollowupId = patient?.PatientFollowups?.find(
    (pf) => pf.id === patientFollowupIdToEdit,
  )?.FollowupId
  const currentFollowup = currentFollowupId ? getFollowupById(currentFollowupId) : undefined

  const hasFormBeenModifiedRef = useRef<boolean>(false)

  // Track whether the followup was ever successfully resolved during this modal's lifetime.
  // If the backend deletes the PatientFollowup (it became empty after all med events were
  // removed), the React Query cache update re-renders this component with a missing followup
  // while the modal is still mounted. Without this guard, ErrorQueryHandler throws, causing
  // React to surface a "not found" error before the modal closes.
  const wasFollowupFoundRef = useRef(false)
  if (currentFollowupId && currentFollowup) {
    wasFollowupFoundRef.current = true
  }

  const [showWarningModal, setShowWarningModal] = useState(false)

  const setHasFormBeenModified = useCallback((value: boolean) => {
    hasFormBeenModifiedRef.current = value
  }, [])

  const handleOnToggle = (event?: React.MouseEvent<HTMLElement>, isSubmittingForm?: boolean) => {
    if (hasFormBeenModifiedRef.current && !isSubmittingForm) {
      setShowWarningModal(true)
    } else {
      onToggle(event)
      setShowWarningModal(false)
      hasFormBeenModifiedRef.current = false
    }
  }

  const handleConfirmClose = () => {
    setShowWarningModal(false)
    hasFormBeenModifiedRef.current = false
    onToggle()
  }

  const handleCancelClose = () => {
    setShowWarningModal(false)
  }

  // Close the modal if the followup disappears after having been found (backend deleted it).
  useEffect(() => {
    if (
      !isLoadingPatient &&
      patient &&
      wasFollowupFoundRef.current &&
      (!currentFollowupId || !currentFollowup)
    ) {
      onToggle()
    }
  }, [isLoadingPatient, patient, currentFollowupId, currentFollowup, onToggle])

  if (errorPatient) {
    return <ErrorQueryHandler error={errorPatient} />
  }

  if (isLoadingPatient) {
    return <div className="loading" />
  }

  if (!currentFollowupId || !currentFollowup) {
    // Return null while the useEffect above triggers the close — avoids throwing an error
    // mid-render if the followup was just deleted by the backend.
    if (wasFollowupFoundRef.current) {
      return null
    }
    return <ErrorQueryHandler error={t('components.generic-followups-form.project-followup-not-found')} />
  }

  return (
    <>
      <CurecallModalContainer
        closeModalAriaLabel={t('common.actions.close')}
        title={t('components.generic-followups-form.update', { followupName: currentFollowup.name })}
        isOpen={true}
        toggle={handleOnToggle}
        dialogClassName="c-w-[72vw] c-max-w-[1300px]"
        bodyClassName="tw-px-6 !c-pb-0"
      >
        <FollowupModalContext.Provider value={{ setHasFormBeenModified, handleOnToggle }}>
          <GenericFollowupFormComponent
            followup={currentFollowup}
            patient={patient}
            patientFollowupIdEdited={patientFollowupIdToEdit}
            onCancel={handleOnToggle}
          />
        </FollowupModalContext.Provider>
      </CurecallModalContainer>

      {showWarningModal && (
        <CurecallConfirmationModal
          isOpen={showWarningModal}
          onCancel={handleCancelClose}
          onConfirm={handleConfirmClose}
          title={t('components.generic-followups-form.closing-title')}
          confirmButtonLabel={t('common.actions.confirm')}
          cancelButtonLabel={t('common.actions.cancel')}
          color="ALERT"
        >
          {t('components.generic-followups-form.warning-closing-without-saving')}
        </CurecallConfirmationModal>
      )}
    </>
  )
}

export default GenericFollowupModal
